/* =============================================================================
   ODETTE COLLECTIVE — Custom JS
   Project-specific behaviour layered on top of the Sleek theme.
   This file is not part of the Sleek theme and will not be overwritten on update.
   ============================================================================= */

/* -----------------------------------------------------------------------------
   Compact sticky header
   When the secondary nav section scrolls out of view, switch the sticky header
   to a compact layout: logo left, secondary nav links replace the main nav.
   ----------------------------------------------------------------------------- */

import("./form.js");

class CompactHeader {
  /* How far past the secondary nav section you must scroll before the compact
     header can appear. Keeps the header copy from showing while the real
     section is still on screen or barely out of it. */
  static SWAP_OFFSET = 120;

  /* Px of travel against the current direction before a reversal counts. */
  static DIRECTION_THRESHOLD = 10;

  /* Fade-out of the full-width header before the compact grid applies —
     matches --duration-default. */
  static ENTER_FADE_MS = 200;

  constructor() {
    this.secondaryNavSection = document.querySelector(
      ".shopify-section-secondary-nav",
    );
    if (!this.secondaryNavSection) return;

    this.headerSection = document.querySelector(".header-section");
    this.headerNav = this.headerSection?.querySelector(
      ".header__navigation:not(.header__navigation--split)",
    );
    if (!this.headerNav) return;

    this.logo = this.headerSection.querySelector(".header__logo");
    this.injected = false;
    this._pending = null;
    this._desired = false;
    this._entering = false;
    this._exiting = false;
    this._exitTimeout = null;
    this._exitFallback = null;
    this._onExitEnd = null;
    this._logoListener = null;
    this._scrollRaf = null;
    this._direction = "down";
    this._anchorY = Math.max(0, window.scrollY);

    // Enable our CSS rules only when a secondary nav section is actually present.
    document.body.classList.add("compact-header-enabled");

    this.observe();
  }

  injectSecondaryNav() {
    if (this.injected) return;

    const list = this.secondaryNavSection.querySelector(".secondary-nav__list");
    if (!list) return;

    const clone = list.cloneNode(true);
    clone.classList.remove("page-width");
    clone.classList.add("header__secondary-nav");

    this.headerNav.appendChild(clone);
    this.injected = true;
  }

  /* Slide logo in from offscreen left into its new grid position */
  flipLogoIn(applyFn) {
    if (!this.logo) return applyFn();

    const first = this.logo.getBoundingClientRect();
    applyFn();
    const last = this.logo.getBoundingClientRect();

    if (first.left === last.left) return;

    const offscreen = -(last.right + 20);
    const logo = this.logo;

    this.clearLogoListener();

    logo.style.transition = "none";
    logo.style.transform = `translateX(${offscreen}px)`;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        logo.style.transition = `transform var(--animation-nav)`;
        logo.style.transform = "translateX(0)";
      });
    });

    // Track so an exit starting mid-slide can remove it.
    const onEnterEnd = (e) => {
      if (e.target !== logo || e.propertyName !== "transform") return;
      this.clearLogoListener();
      logo.style.transition = "";
      logo.style.transform = "";
    };

    this._logoListener = onEnterEnd;
    logo.addEventListener("transitionend", onEnterEnd);
  }

  /* Detach whichever logo transitionend handler is currently armed. */
  clearLogoListener() {
    if (this._logoListener && this.logo) {
      this.logo.removeEventListener("transitionend", this._logoListener);
    }
    this._logoListener = null;
  }

  /* Exit: logo slides further left and fades out, everything fades in centered. */
  exitLogoLeft(applyFn) {
    if (!this.logo) return applyFn();

    const logo = this.logo;

    // Drop any enter-slide listener still armed from an interrupted flipLogoIn
    this.clearLogoListener();

    // Always fade the cloned secondary nav alongside the exit.
    const clonedSecondary = this.headerNav.querySelector(
      ".header__secondary-nav",
    );
    if (clonedSecondary) {
      clonedSecondary.style.animation = "none";
      this._clonedFadeAnim?.cancel();
      this._clonedFadeAnim = clonedSecondary.animate(
        [{ opacity: 1 }, { opacity: 0 }],
        {
          duration: 500,
          easing: "cubic-bezier(.6, 0, .4, 1)",
          fill: "forwards",
        },
      );
    }

    // Detect whether the logo would actually move when past-secondary-nav is removed
    const beforeLeft = logo.getBoundingClientRect().left;
    document.body.classList.remove("past-secondary-nav");
    const afterLeft = logo.getBoundingClientRect().left;
    document.body.classList.add("past-secondary-nav");
    const logoMoves = Math.abs(beforeLeft - afterLeft) > 1;

    if (!logoMoves) {
      // Logo stays in place — wait for the cloned-nav fade-out, then swap and
      // fade in the rest. No logo animation.
      this._exitTimeout = setTimeout(
        () => this._completeExit(applyFn, /* includeLogo */ false),
        500,
      );
      return;
    }

    logo.style.transition = `transform var(--animation-nav), opacity var(--animation-nav)`;
    logo.style.transform = "translateX(-60px)";
    logo.style.opacity = "0";

    const finish = () => {
      if (!this._exiting) return; // already aborted or completed
      logo.removeEventListener("transitionend", onExitEnd);
      this._onExitEnd = null;
      clearTimeout(this._exitFallback);
      this._exitFallback = null;

      // Snap logo back to its grid position
      logo.style.transition = "none";
      logo.style.transform = "";
      logo.style.opacity = "";

      this._completeExit(applyFn, /* includeLogo */ true);
    };

    const onExitEnd = (e) => {
      // Ignore transitions bubbling up from children (e.g. the logo image)
      if (e.target !== logo || e.propertyName !== "opacity") return;
      finish();
    };

    this._onExitEnd = onExitEnd;
    logo.addEventListener("transitionend", onExitEnd);

    // Safety net: if the opacity transitionend never arrives finish anyway.
    clearTimeout(this._exitFallback);
    this._exitFallback = setTimeout(finish, 800);
  }

  /* Abort an exit that is still mid-flight */
  abortExit() {
    if (!this._exiting) return;
    this._exiting = false;

    clearTimeout(this._exitTimeout);
    this._exitTimeout = null;
    clearTimeout(this._exitFallback);
    this._exitFallback = null;

    if (this._onExitEnd && this.logo) {
      this.logo.removeEventListener("transitionend", this._onExitEnd);
      this._onExitEnd = null;
    }
    this.clearLogoListener();

    // Snap the logo back to its compact grid position without a transition
    if (this.logo) {
      this.logo.style.transition = "none";
      this.logo.style.transform = "";
      this.logo.style.opacity = "";
      void this.logo.offsetHeight; // commit before restoring transitions
      this.logo.style.transition = "";
    }

    // Fade the cloned secondary nav back in from wherever its fade-out got to
    const clonedSecondary = this.headerNav.querySelector(
      ".header__secondary-nav",
    );
    if (clonedSecondary) {
      const from = parseFloat(getComputedStyle(clonedSecondary).opacity) || 0;
      this._clonedFadeAnim?.cancel();
      this._clonedFadeAnim = clonedSecondary.animate(
        [{ opacity: from }, { opacity: 1 }],
        {
          duration: 300,
          easing: "cubic-bezier(.6, 0, .4, 1)",
          fill: "forwards",
        },
      );
    } else {
      this._clonedFadeAnim?.cancel();
      this._clonedFadeAnim = null;
    }

    document.body.classList.remove("compact-fading");
    document.body.classList.add("past-secondary-nav");
  }

  _completeExit(applyFn, includeLogo) {
    applyFn(); // removes past-secondary-nav → all elements become visible

    this._clonedFadeAnim?.cancel();
    this._clonedFadeAnim = null;

    // Force browser to commit display change before attaching animations
    void document.body.offsetHeight;

    const elements = [
      includeLogo ? this.logo : null,
      this.headerSection.querySelector(".header__navigation--split"),
      this.headerSection.querySelector(".header__icons--left"),
      this.headerNav.querySelector(".header__menu"),
    ].filter(Boolean);

    requestAnimationFrame(() => {
      elements.forEach((el) => {
        el.animate([{ opacity: 0 }, { opacity: 1 }], {
          duration: 500,
          easing: "cubic-bezier(.6, 0, .4, 1)",
          fill: "backwards",
        });
      });
    });
  }

  /* ---------------------------------------------------------------------------
     State driver

     The desired state is DERIVED on every frame from scroll DIRECTION rather than
     latched by boundary-crossing events. Scrolling down anywhere past the section
     brings the secondary nav into the header; scrolling up anywhere brings the
     main nav back. Position is only a veto near the top of the page.
     --------------------------------------------------------------------------- */

  /* Track scroll direction with a deadzone, anchored to the furthest point
     reached in the current direction. */
  updateDirection() {
    const y = Math.max(0, window.scrollY);
    const T = CompactHeader.DIRECTION_THRESHOLD;

    if (this._direction === "down") {
      if (y > this._anchorY) {
        this._anchorY = y; // still descending — push the anchor along
      } else if (this._anchorY - y > T) {
        this._direction = "up";
        this._anchorY = y;
      }
    } else {
      if (y < this._anchorY) {
        this._anchorY = y;
      } else if (y - this._anchorY > T) {
        this._direction = "down";
        this._anchorY = y;
      }
    }
  }

  /* Compact mode is driven by DIRECTION, not position */
  shouldBeCompact() {
    if (this._direction !== "down") return false;

    const rect = this.secondaryNavSection.getBoundingClientRect();
    return rect.bottom < -CompactHeader.SWAP_OFFSET;
  }

  update() {
    this.updateDirection();
    this.setCompact(this.shouldBeCompact());
  }

  setCompact(compact) {
    if (this._desired === compact) return;
    this._desired = compact;

    if (compact) {
      // A half-finished exit still has the compact layout underneath it
      if (this._exiting) return this.abortExit();
      if (this._entering) return; // already on its way
      this.enter();
    } else {
      // A half-finished enter has not swapped the layout yet
      if (this._entering) return this.abortEnter();
      if (this._exiting) return; // already on its way
      this.exit();
    }
  }

  enter() {
    this.injectSecondaryNav();

    // Clear any inline state a previous exit left on the clone
    // so the CSS fade-in runs fresh.
    const clone = this.headerNav.querySelector(".header__secondary-nav");
    if (clone) {
      this._clonedFadeAnim?.cancel();
      this._clonedFadeAnim = null;
      clone.style.animation = "";
      clone.style.opacity = "";
    }

    this._entering = true;
    document.body.classList.add("compact-fading");

    this._pending = setTimeout(() => {
      this._pending = null;
      this._entering = false;
      this.flipLogoIn(() => {
        document.body.classList.remove("compact-fading");
        document.body.classList.add("past-secondary-nav");
      });
    }, CompactHeader.ENTER_FADE_MS);
  }

  /* Cancel an enter that has only faded the full header out — the compact grid
     was never applied, so there is nothing to animate back. */
  abortEnter() {
    if (!this._entering) return;
    this._entering = false;

    clearTimeout(this._pending);
    this._pending = null;
    document.body.classList.remove("compact-fading");
  }

  exit() {
    if (this._exiting) return;
    this._exiting = true;
    this.exitLogoLeft(() => {
      document.body.classList.remove("compact-fading", "past-secondary-nav");
      this._exiting = false;
    });
  }

  /* Apply the correct state on load with no animation — the page can be restored
     mid-scroll, where compact is already the right answer. */
  applyInitialState() {
    this._desired = this.shouldBeCompact();
    if (!this._desired) return;

    this.injectSecondaryNav();
    document.body.classList.add("past-secondary-nav");
  }

  observe() {
    this.applyInitialState();

    window.addEventListener(
      "scroll",
      () => {
        if (this._scrollRaf) return;
        this._scrollRaf = requestAnimationFrame(() => {
          this._scrollRaf = null;
          this.update();
        });
      },
      { passive: true },
    );

    // Scroll events alone can go stale: late-loading images and font swaps move
    // the section without any scrolling, changing the answer.
    if ("ResizeObserver" in window) {
      const ro = new ResizeObserver(() => this.update());
      ro.observe(this.secondaryNavSection);
      ro.observe(document.documentElement);
    }
    window.addEventListener("resize", () => this.update(), { passive: true });
  }
}

document.addEventListener("page:loaded", () => new CompactHeader());

/* -----------------------------------------------------------------------------
   Custom split menus

   The theme's center-split header takes one menu and splits its links in half.
   When the `custom-header-split-menus` section provides BOTH a left and a right
   menu, replace each half with the full contents of the chosen menu. If only
   one (or neither) is provided, leave the theme's default split behaviour alone.
   ----------------------------------------------------------------------------- */
class CustomSplitMenus {
  constructor() {
    const headerSection = document.querySelector(".header-section");
    if (!headerSection) return;

    const leftTpl = document.getElementById("custom-header-left-menu");
    const rightTpl = document.getElementById("custom-header-right-menu");

    // Nothing provided by the section at all — leave the theme default alone.
    if (!leftTpl && !rightTpl) {
      console.warn(
        "[CustomSplitMenus] No menu templates found. Add the 'Custom split menus' section to the header group, or check that its Left/Right menus are set.",
      );
      return;
    }

    const leftNav = headerSection.querySelector(
      ".header__navigation:not(.header__navigation--split)",
    );
    let rightNav = headerSection.querySelector(".header__navigation--split");

    // Swap each side INDEPENDENTLY so a missing/deleted menu (or missing nav
    // container) on one side never suppresses the other.
    if (leftTpl) {
      if (leftNav) {
        this.swap(leftNav, leftTpl);
      } else {
        console.warn("[CustomSplitMenus] Left nav container not found.");
      }
    } else {
      console.warn(
        "[CustomSplitMenus] Left menu template missing — the section's 'Left menu' is unset or its menu was deleted. Keeping the theme default on the left.",
      );
    }

    if (rightTpl) {
      // The theme only outputs the split-nav container when the header's OWN
      // "Menu" setting is non-blank (header.liquid:204). A deleted/blank header
      // menu therefore removes the right column entirely. Recreate it so the
      // custom right menu no longer depends on that setting.
      if (!rightNav) {
        rightNav = this.ensureSplitNav(headerSection, leftNav);
      }
      if (rightNav) {
        this.swap(rightNav, rightTpl);
      } else {
        console.warn(
          "[CustomSplitMenus] Right menu set but the header layout isn't 'Center split', so there's no split column to place it in.",
        );
      }
    } else {
      console.warn(
        "[CustomSplitMenus] Right menu template missing — the section's 'Right menu' is unset or its menu was deleted. Keeping the theme default on the right.",
      );
    }

    const config = document.getElementById("custom-split-menus-config");
    const position = config?.dataset.menuPosition || "center";
    document.body.classList.toggle("custom-split--edges", position === "edges");
    document.body.classList.toggle(
      "custom-split--center",
      position !== "edges",
    );

    // Edges mode: track the right icons width and mirror it as a CSS var so
    // a leading spacer column can keep the logo visually centered. Guard so the
    // observer/listener are only attached once even if init runs again.
    if (position === "edges" && !this.constructor._edgesTracked) {
      const icons = headerSection.querySelector(".header__icons--right");
      if (icons) {
        this.constructor._edgesTracked = true;
        const setWidth = () => {
          const w = Math.round(icons.getBoundingClientRect().width);
          document.body.style.setProperty("--header-icons-width", `${w}px`);
        };
        setWidth();
        if (typeof ResizeObserver !== "undefined") {
          new ResizeObserver(setWidth).observe(icons);
        }
        window.addEventListener("resize", setWidth);
      }
    }
  }

  /* Create the split-nav grid column when the theme didn't render it */
  ensureSplitNav(headerSection, leftNav) {
    const headerEl = headerSection.querySelector(".header");
    if (!headerEl || !headerEl.classList.contains("header--center-split")) {
      return null;
    }
    const nav = document.createElement("div");
    nav.className =
      "header__navigation header__navigation--split hidden lg:flex";
    const parent = leftNav?.parentElement || headerEl;
    parent.appendChild(nav);
    return nav;
  }

  /* Replace only the .header__menu child, preserving other siblings
       (e.g. the secondary-nav clone injected by CompactHeader). */
  swap(container, template) {
    const oldMenu = container.querySelector(".header__menu");
    const content = template.content.cloneNode(true);
    if (oldMenu) {
      oldMenu.replaceWith(content);
    } else {
      container.appendChild(content);
    }
  }
}

function initCustomSplitMenus() {
  new CustomSplitMenus();
}

document.addEventListener("page:loaded", initCustomSplitMenus);

/* Fallback: page:loaded may have already fired (or not fire) by the time this
   deferred script runs — run once the DOM is ready regardless. */
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initCustomSplitMenus);
} else {
  initCustomSplitMenus();
}

/* Re-run when the section (or the header it lives in) is re-rendered in the
   theme editor. Force a fresh swap because the theme re-renders default menus. */
document.addEventListener("shopify:section:load", (e) => {
  const t = e.target;
  if (
    t.classList?.contains("custom-split-menus-section") ||
    t.querySelector?.("#custom-split-menus-config") ||
    t.querySelector?.(".header-section")
  ) {
    CustomSplitMenus._edgesTracked = false;
    initCustomSplitMenus();
  }
});

/* Mobile menu drawer - Inject secondary nav below the main menu */
class MobileMenuSecondaryNav {
  constructor() {
    const source = document.querySelector(
      ".shopify-section-secondary-nav .secondary-nav__list",
    );
    if (!source) return;

    const mainList = document.querySelector("#MenuDrawer .menu-drawer__menus");
    if (!mainList) return;

    // Remove any items we previously injected so re-runs don't duplicate
    mainList
      .querySelectorAll(".menu-drawer__secondary-item")
      .forEach((el) => el.remove());

    const items = Array.from(source.children).map((li) => {
      const clone = li.cloneNode(true);
      clone.classList.add("menu-drawer__secondary-item");

      // Flatten <details> dropdowns to flat links — the parent URL lives on
      // summary[data-link]. The drawer doesn't surface secondary-nav submenus.
      clone.querySelectorAll("details").forEach((details) => {
        const summary = details.querySelector("summary");
        const href = summary?.dataset.link || "#";
        const label =
          summary?.querySelector(".secondary-nav__link")?.textContent?.trim() ||
          summary?.textContent?.trim() ||
          "";
        const link = document.createElement("a");
        link.href = href;
        link.className = "secondary-nav__link font-navigation reversed-link";
        link.textContent = label;
        details.replaceWith(link);
      });

      return clone;
    });

    // Mark the first injected item so CSS can render a separator above it
    items[0]?.classList.add("menu-drawer__secondary-item--first");

    items.forEach((item) => mainList.appendChild(item));
  }
}

document.addEventListener("page:loaded", () => new MobileMenuSecondaryNav());

/* Re-sync when the secondary-nav section is re-rendered in the theme editor */
document.addEventListener("shopify:section:load", (e) => {
  if (e.target.classList.contains("shopify-section-secondary-nav")) {
    new MobileMenuSecondaryNav();
  }
});

/* -----------------------------------------------------------------------------
   On Hold status

   A product tagged `tag__onhold_On Hold` shows the On Hold badge (rendered
   server-side by the theme's tag system) and must not be purchasable. The badge
   alone can't disable buying, so this module disables the Add to Cart controls
   on product cards, the product page, and the quick-view modal.
   ----------------------------------------------------------------------------- */
(function () {
  const ON_HOLD = {
    tagPrefix: "tag__onhold", // matches the `tag__onhold_*` product tag
    badgeClass: "f-badge--onhold",
    label: "On Hold",
  };

  /* Cache: product url (path only) -> Promise<boolean of has on-hold tag>.
       Ensures each product is fetched at most once across the PDP and any number
       of quick-view opens. */
  const tagCache = new Map();

  function isOnHoldByTags(url) {
    if (!url) return Promise.resolve(false);
    const key = url.split("?")[0];
    if (!tagCache.has(key)) {
      tagCache.set(
        key,
        fetch(`${key}.js`)
          .then((r) => (r.ok ? r.json() : null))
          .then((data) =>
            Array.isArray(data && data.tags)
              ? data.tags.some((t) =>
                  String(t).toLowerCase().startsWith(ON_HOLD.tagPrefix),
                )
              : false,
          )
          .catch(() => false),
      );
    }
    return tagCache.get(key);
  }

  /* Disable purchasing within a <product-info> scope (PDP or quick view). */
  function applyOnHold(scope) {
    if (!scope || scope.dataset.onHoldApplied) return;
    scope.dataset.onHoldApplied = "true";
    scope.classList.add("is-on-hold");

    const submit = scope.querySelector(".product-form__submit");
    if (submit) {
      submit.disabled = true;
      const label = submit.querySelector("span");
      if (label) label.textContent = ON_HOLD.label;
    }

    // Inject the badge at the top of the info column if one isn't already there.
    if (!scope.querySelector("." + ON_HOLD.badgeClass)) {
      const container =
        scope.querySelector(".product__info-container") || scope;
      const wrap = document.createElement("div");
      wrap.className =
        "product__block product__badges on-hold-injected flex flex-wrap items-center";
      const badge = document.createElement("span");
      badge.className = "f-badge " + ON_HOLD.badgeClass;
      badge.textContent = ON_HOLD.label;
      wrap.appendChild(badge);
      container.prepend(wrap);
    }

    // Safety net for keyboard submits — dynamic checkout buttons are hidden by
    // CSS, the ATC button is disabled, but block the form just in case.
    const form = scope.querySelector('form[is="product-form"]');
    if (form) {
      form.addEventListener("submit", (e) => {
        e.preventDefault();
        e.stopImmediatePropagation();
      });
    }
  }

  /* Mark each on-hold card and disable its quick-add / choose-options control. */
  function processCards(root) {
    (root || document)
      .querySelectorAll("." + ON_HOLD.badgeClass)
      .forEach((badge) => {
        const card = badge.closest(".product-card");
        if (!card || card.dataset.onHoldApplied) return;
        card.dataset.onHoldApplied = "true";
        card.classList.add("is-on-hold");

        card.querySelectorAll(".product-card__atc").forEach((el) => {
          if (el.tagName === "A") {
            el.setAttribute("aria-disabled", "true");
            el.addEventListener("click", (e) => e.preventDefault());
          } else {
            el.disabled = true;
            const text = el.querySelector(".product-card__atc-text");
            if (text) text.textContent = ON_HOLD.label;
          }
        });
      });
  }

  /* Product page — one cached fetch, then disable if on hold. */
  function processProductPage() {
    const scope = document.querySelector(
      'product-info[data-url]:not([data-view-mode="quick-view"])',
    );
    if (!scope) return;
    isOnHoldByTags(scope.dataset.url).then((onHold) => {
      if (onHold) applyOnHold(scope);
    });
  }

  /* Quick view — fires after the modal's AJAX content is injected. */
  function onQuickViewLoaded(e) {
    const url = e.detail && e.detail.productUrl;
    const modal = url
      ? document.querySelector(`quick-view-modal[data-product-url="${url}"]`)
      : null;
    const scope = (modal || document).querySelector(
      'product-info[data-view-mode="quick-view"]',
    );
    if (!scope) return;
    isOnHoldByTags(url).then((onHold) => {
      if (onHold) applyOnHold(scope);
    });
  }

  function init() {
    processCards(document);
    processProductPage();
  }

  document.addEventListener("page:loaded", init);
  document.addEventListener("DOMContentLoaded", init);
  document.addEventListener("quick-view:loaded", onQuickViewLoaded);

  /* Re-scan cards when a section is re-rendered in the theme editor */
  document.addEventListener("shopify:section:load", () =>
    processCards(document),
  );
})();

/* -----------------------------------------------------------------------------
   Pickup availability — "Call to schedule pickup"
   Appends "Call 205.324.7613 to schedule pickup." to the "Usually ready in …"
   line. Kept here rather than in sections/pickup-availability.liquid (a core
   Sleek file). The theme fetches and re-renders that block on every variant
   change, so a MutationObserver re-applies the line after each render. The
   number is the one the client specified, not the pickup location's phone.
   ----------------------------------------------------------------------------- */

(() => {
  const PHONE = "205.324.7613";
  const TEL = "tel:+12053247613";

  function addCallLine(line) {
    line.dataset.callAdded = "";

    const text = line.textContent.trim();
    const call = document.createElement("span");
    call.className = "pickup-availability-info__call";
    call.append(/[.!?]$/.test(text) ? " Call " : ". Call ");

    const link = document.createElement("a");
    link.href = TEL;
    link.textContent = PHONE;
    call.append(link, " to schedule pickup.");
    line.append(call);
  }

  function scan() {
    document
      .querySelectorAll(
        ".pickup-availability-info__pickup-time:not([data-call-added])",
      )
      .forEach(addCallLine);
  }

  scan();
  new MutationObserver(scan).observe(document.body, {
    childList: true,
    subtree: true,
  });
})();
